import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, LabelList } from 'recharts';

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `€${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `€${(value / 1000).toFixed(0)}K`;
  }
  return `€${value.toFixed(0)}`;
};

const formatCurrencyFull = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-semibold text-slate-800 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrencyFull(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomBarLabel = ({ x, y, width, value }) => {
  if (!value || value < 1000) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#494751"
      textAnchor="middle"
      fontSize={10}
      fontWeight="500"
    >
      {formatCurrency(value)}
    </text>
  );
};

const CustomLineLabel = ({ x, y, value }) => {
  if (!value) return null;
  return (
    <text
      x={x}
      y={y - 10}
      fill="#494751"
      textAnchor="middle"
      fontSize={10}
      fontWeight="500"
    >
      {formatCurrency(value)}
    </text>
  );
};

export default function DebtEvolutionCharts({ proyeccionesData, capitalPendienteInicial }) {
  if (!proyeccionesData || proyeccionesData.length === 0) {
    return null;
  }

  // Preparar datos para el gráfico de barras (amortización + intereses por año)
  const barChartData = proyeccionesData.map(item => ({
    año: item.año.toString(),
    Amortización: item.totalAmortizacion,
    Intereses: item.totalInteres,
  }));

  // Preparar datos para el gráfico de línea (capital pendiente por año)
  // El capital pendiente inicial es el que tenemos actualmente (suma de capital_pendiente de todas las operaciones LP)
  // Cada año se resta la amortización de ese año
  const lineChartData = [];
  let capitalRestante = capitalPendienteInicial || proyeccionesData.reduce((sum, item) => sum + item.totalAmortizacion, 0);
  
  // Primer punto: capital pendiente actual (inicio del período de proyección)
  const primerAño = proyeccionesData[0]?.año || new Date().getFullYear();
  lineChartData.push({
    año: `${primerAño} (inicio)`,
    'Capital Pendiente': capitalRestante
  });

  // Para cada año, mostrar el capital que queda AL FINAL del año (después de pagar)
  proyeccionesData.forEach(item => {
    capitalRestante -= item.totalAmortizacion;
    lineChartData.push({
      año: item.año.toString(),
      'Capital Pendiente': Math.max(0, capitalRestante)
    });
  });

  return (
    <div className="mt-8" data-testid="evolution-charts">
      <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">
        Evolución de la Deuda a Largo Plazo
      </h2>
      <p className="text-slate-500 mb-6">
        Proyección de amortización e intereses de los préstamos a largo plazo.
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="evolution-charts-grid">
        {/* Gráfico de Barras - Pago Anual */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="bar-chart-container">
          <h3 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-4">
            Pago Anual (Amortización + Intereses)
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E1DA" />
                <XAxis 
                  dataKey="año" 
                  tick={{ fill: '#757070', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E1DA' }}
                />
                <YAxis 
                  tickFormatter={formatCurrency}
                  tick={{ fill: '#757070', fontSize: 11 }}
                  axisLine={{ stroke: '#E5E1DA' }}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                />
                <Bar 
                  dataKey="Amortización"
                  fill="#31313D"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                >
                  <LabelList content={<CustomBarLabel />} position="top" />
                </Bar>
                <Bar 
                  dataKey="Intereses"
                  fill="#F6AA19"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                >
                  <LabelList content={<CustomBarLabel />} position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico de Línea - Capital Pendiente */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="line-chart-container">
          <h3 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-4">
            Capital Pendiente a Largo Plazo
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E1DA" />
                <XAxis 
                  dataKey="año" 
                  tick={{ fill: '#757070', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E1DA' }}
                />
                <YAxis 
                  tickFormatter={formatCurrency}
                  tick={{ fill: '#757070', fontSize: 11 }}
                  axisLine={{ stroke: '#E5E1DA' }}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="Capital Pendiente" 
                  stroke="#F6AA19"
                  strokeWidth={2}
                  dot={{ fill: '#F6AA19', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: '#F6AA19' }}
                >
                  <LabelList content={<CustomLineLabel />} position="top" />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
